import { useQuery } from '@apollo/client';
import { Dialog, Transition } from '@headlessui/react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import {
  Badge,
  timestampFormatter,
  underscoreFormatter,
  warrantyStateColor,
} from '@retainerclub/common';
import { GET_WARRANTY } from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';

const mapUserIssue = (value) => {
  const mapping = {
    does_not_fit: "Doesn't fit properly",
    manufacturing_issue: 'Manufacturing issue / defect',
  };
  return mapping[value] || value || '—';
};

const mapArch = (value) => {
  const mapping = {
    upper: 'Top retainer',
    lower: 'Bottom retainer',
    both: 'Both retainers',
  };
  return mapping[value] || value || '—';
};

const mapArchFit = (value) => {
  const mapping = {
    upper_good_fit: 'Good fit',
    upper_too_tight: 'Too tight',
    upper_too_loose: 'Too loose',
    upper_doesnt_cover_back_teeth: "Doesn't cover back teeth",
    upper_rubs_on_gums: 'Rubs on gums',
    upper_other: 'Other',
    lower_good_fit: 'Good fit',
    lower_too_tight: 'Too tight',
    lower_too_loose: 'Too loose',
    lower_doesnt_cover_back_teeth: "Doesn't cover back teeth",
    lower_rubs_on_gums: 'Rubs on gums',
    lower_other: 'Other',
  };
  return mapping[value] || value || '—';
};

// Humanizes the raw Warranty state enum. The previous local mapping only
// covered pending/approved/rejected plus three states that do not exist in
// Warranties::StateMachine, so every review state rendered with its underscores
// showing.
const mapWarrantyState = (value) => {
  if (!value) {
    return '—';
  }

  return underscoreFormatter(value);
};

function CustomerWarrantyViewModal({
  isOpen,
  onClose,
  warrantyId,
  warranties,
}) {
  const [viewMode, setViewMode] = useState('detail');
  const [selectedWarrantyId, setSelectedWarrantyId] = useState(null);

  const hasMultiple = warranties && warranties.length > 1;
  const effectiveId =
    selectedWarrantyId ||
    warrantyId ||
    (warranties?.length === 1 ? warranties[0].id : null);
  const showListFirst = hasMultiple && viewMode === 'list';

  useEffect(() => {
    if (!isOpen) {
      setViewMode('detail');
      setSelectedWarrantyId(null);
      return;
    }
    if (hasMultiple) {
      setViewMode('list');
      setSelectedWarrantyId(null);
    } else {
      setViewMode('detail');
      setSelectedWarrantyId(null);
    }
  }, [isOpen, hasMultiple]);

  const { data, loading, error } = useQuery(GET_WARRANTY, {
    variables: { id: effectiveId },
    skip: !(effectiveId && isOpen && !showListFirst),
  });

  const warranty = data?.warranty;

  function handleSelectFromList(id) {
    setSelectedWarrantyId(id);
    setViewMode('detail');
  }

  function handleBackToList() {
    setSelectedWarrantyId(null);
    setViewMode('list');
  }

  function getDetailViewState() {
    if (isOpen && !effectiveId) {
      return 'error';
    }
    if (loading) {
      return 'loading';
    }
    if (error) {
      return 'error';
    }
    if (!warranty) {
      return data !== undefined ? 'error' : 'loading';
    }
    if (effectiveId && warranty.id !== effectiveId) {
      return 'loading';
    }
    return 'detail';
  }

  function renderLoading() {
    return (
      <Transition.Root as={Fragment} show={isOpen}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="rounded-lg bg-white px-6 py-8 shadow-xl sm:w-full sm:max-w-md">
                <p className="text-center text-gray-500 text-sm">
                  Loading your PFG details...
                </p>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  function renderError() {
    return (
      <Transition.Root as={Fragment} show={isOpen}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="rounded-lg bg-white px-6 py-8 shadow-xl sm:w-full sm:max-w-md">
                <p className="text-center text-red-600 text-sm">
                  Unable to load PFG details. Please try again.
                </p>
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    className="rounded-md border border-transparent bg-green-600 px-4 py-2 font-medium text-sm text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  if (showListFirst) {
    const sortedWarranties = [...warranties].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
    return (
      <Transition.Root as={Fragment} show={isOpen}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full transform overflow-hidden rounded-xl bg-white shadow-xl transition-all sm:max-w-md">
                  <div className="flex items-center justify-between border-gray-200 border-b px-5 py-4">
                    <Dialog.Title
                      as="h2"
                      className="font-semibold text-gray-900 text-lg"
                    >
                      Your Perfect Fit Guarantees
                    </Dialog.Title>
                    <button
                      type="button"
                      className="rounded-md text-gray-500 hover:bg-rcprimary-50 hover:text-rcprimary-600 focus:outline-none focus:ring-2 focus:ring-rcprimary-500"
                      onClick={onClose}
                      aria-label="Close"
                    >
                      <XMarkIcon aria-hidden className="size-5" />
                    </button>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
                    <p className="mb-3 text-gray-500 text-sm">
                      Select a PFG to view details
                    </p>
                    <ul className="space-y-2">
                      {sortedWarranties.map((w) => (
                        <li key={w.id}>
                          <button
                            type="button"
                            onClick={() => handleSelectFromList(w.id)}
                            className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-rcprimary-500 focus:ring-offset-2"
                          >
                            <div className="min-w-0 flex-1">
                              <span className="font-semibold text-gray-900 text-sm">
                                Case {w.hexCode?.toUpperCase() ?? '—'}
                              </span>
                              <Badge
                                className="ml-2"
                                color={warrantyStateColor(w.state) || 'gray'}
                                name={mapWarrantyState(w.state)}
                                size="small"
                                withDot={false}
                              />
                              <p className="mt-0.5 text-gray-500 text-xs">
                                Submitted {timestampFormatter(w.createdAt)}
                              </p>
                            </div>
                            <ChevronRightIcon
                              aria-hidden
                              className="size-5 shrink-0 text-gray-400"
                            />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="border-gray-200 border-t px-5 py-4">
                    <button
                      type="button"
                      className="w-full rounded-md border border-transparent bg-green-600 px-4 py-2 font-medium text-sm text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  const detailViewState = getDetailViewState();
  if (detailViewState === 'loading') {
    return renderLoading();
  }
  if (detailViewState === 'error') {
    return renderError();
  }

  const remakeOrders = warranty.productionOrders || [];
  const trackingItems = remakeOrders.flatMap((po) =>
    (po.productionOrderItems || [])
      .filter((item) => item.trackingNumber || item.trackingUrl)
      .map((item) => ({
        hexCode: po.hexCode,
        carrier: item.shippingCarrier?.name,
        trackingNumber: item.trackingNumber,
        trackingUrl: item.trackingUrl,
      })),
  );

  return (
    <Transition.Root as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-xl bg-white shadow-xl transition-all sm:max-w-lg">
                <div className="flex items-center justify-between border-gray-200 border-b px-5 py-4">
                  <Dialog.Title
                    as="h2"
                    className="font-semibold text-gray-900 text-lg"
                  >
                    Your Perfect Fit Guarantee
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-500 hover:bg-rcprimary-50 hover:text-rcprimary-600 focus:outline-none focus:ring-2 focus:ring-rcprimary-500"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <XMarkIcon aria-hidden className="size-5" />
                  </button>
                </div>

                {hasMultiple && (
                  <div className="border-gray-200 border-b px-5 py-2">
                    <button
                      type="button"
                      className="text-left text-rcprimary-600 text-sm hover:underline"
                      onClick={handleBackToList}
                    >
                      ← Back to list
                    </button>
                  </div>
                )}

                <div className="space-y-5 px-5 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900 text-sm">
                      Case {warranty.hexCode?.toUpperCase() ?? '—'}
                    </span>
                    <Badge
                      color={warrantyStateColor(warranty.state) || 'gray'}
                      name={mapWarrantyState(warranty.state)}
                      size="small"
                      withDot={false}
                    />
                  </div>

                  <dl className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_1fr] sm:gap-x-4 sm:gap-y-3">
                    <dt className="text-gray-500 text-sm">Submitted</dt>
                    <dd className="text-gray-900 text-sm">
                      {timestampFormatter(warranty.createdAt)}
                    </dd>

                    <dt className="text-gray-500 text-sm">Issue</dt>
                    <dd className="text-gray-900 text-sm">
                      {mapUserIssue(warranty.userIssue)}
                    </dd>

                    <dt className="text-gray-500 text-sm">Retainer</dt>
                    <dd className="text-gray-900 text-sm">
                      {mapArch(warranty.arch)}
                    </dd>

                    {warranty.arch === 'upper' && (
                      <>
                        <dt className="text-gray-500 text-sm">Fit</dt>
                        <dd className="text-gray-900 text-sm">
                          {mapArchFit(warranty.upperArchFit)}
                        </dd>
                      </>
                    )}
                    {warranty.arch === 'lower' && (
                      <>
                        <dt className="text-gray-500 text-sm">Fit</dt>
                        <dd className="text-gray-900 text-sm">
                          {mapArchFit(warranty.lowerArchFit)}
                        </dd>
                      </>
                    )}
                    {warranty.arch === 'both' && (
                      <>
                        <dt className="text-gray-500 text-sm">Top fit</dt>
                        <dd className="text-gray-900 text-sm">
                          {mapArchFit(warranty.upperArchFit)}
                        </dd>
                        <dt className="text-gray-500 text-sm">Bottom fit</dt>
                        <dd className="text-gray-900 text-sm">
                          {mapArchFit(warranty.lowerArchFit)}
                        </dd>
                      </>
                    )}
                  </dl>

                  {warranty.message?.trim() && (
                    <div>
                      <h3 className="mb-1 font-medium text-gray-700 text-sm">
                        Additional Details
                      </h3>
                      <p className="rounded-md bg-gray-50 px-3 py-2 text-gray-900 text-sm">
                        {warranty.message}
                      </p>
                    </div>
                  )}

                  {warranty.warrantyNotes?.length > 0 && (
                    <div>
                      <h3 className="mb-2 font-medium text-gray-700 text-sm">
                        Notes
                      </h3>
                      <ul className="space-y-2">
                        {[...warranty.warrantyNotes]
                          .sort(
                            (a, b) =>
                              new Date(b.createdAt) - new Date(a.createdAt),
                          )
                          .map((note) => (
                            <li
                              key={note.id}
                              className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                            >
                              <p className="text-gray-900">{note.message}</p>
                              <p className="mt-1 text-gray-500 text-xs">
                                {timestampFormatter(note.createdAt)}
                              </p>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {trackingItems.length > 0 && (
                    <div>
                      <h3 className="mb-2 font-medium text-gray-700 text-sm">
                        Tracking
                      </h3>
                      <ul className="space-y-2">
                        {trackingItems.map((item, idx) => (
                          <li
                            key={`${item.hexCode}-${item.trackingNumber}-${idx}`}
                            className="flex flex-col gap-0.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                          >
                            {item.hexCode && (
                              <span className="font-medium text-gray-700 text-xs">
                                {item.hexCode}
                              </span>
                            )}
                            {item.trackingUrl ? (
                              <a
                                href={item.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-rcprimary-600 hover:underline"
                              >
                                {item.trackingNumber || 'Track shipment'}
                              </a>
                            ) : (
                              <span className="text-gray-900">
                                {item.trackingNumber}
                              </span>
                            )}
                            {item.carrier && (
                              <span className="text-gray-500 text-xs">
                                {item.carrier}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="border-gray-200 border-t px-5 py-4">
                  <button
                    type="button"
                    className="w-full rounded-md border border-transparent bg-green-600 px-4 py-2 font-medium text-sm text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

CustomerWarrantyViewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  warrantyId: PropTypes.string,
  warranties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      hexCode: PropTypes.string,
      state: PropTypes.string,
      createdAt: PropTypes.string,
    }),
  ),
};

export default CustomerWarrantyViewModal;
